import React from 'react';
import { Home, Hammer, Wrench, Construction, Building2, LayoutGrid, ChevronRight } from 'lucide-react';
import { services } from '../mock';

const iconMap = {
  home: Home,
  hammer: Hammer,
  wrench: Wrench,
  construction: Construction,
  building: Building2,
  layout: LayoutGrid
};

export const Services = () => {
  return (
    <section id="services" className="py-20 bg-gray-950">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-yellow-500 font-semibold text-sm uppercase tracking-wider mb-3">
            Unsere Leistungen
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Alles aus einer Hand
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Von der Planung bis zur Fertigstellung – wir bieten Ihnen das komplette Spektrum professioneller
            Handwerksleistungen.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className="group bg-gray-900 rounded-2xl p-8 hover:bg-gray-800 hover:shadow-2xl transition-all duration-300 border border-yellow-500/20 hover:border-yellow-500/50"
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-red-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">{service.description}</p>

                {/* Details List */}
                <ul className="space-y-3 mb-6">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <ChevronRight className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{detail}</span>
                    </li>
                  ))}
                </ul>

                {/* Learn More Link */}
                <button
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center space-x-2 text-yellow-500 font-semibold group-hover:text-yellow-400 transition-colors duration-300"
                >
                  <span>Mehr erfahren</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            );
          })}
        </div>

        {/* CTA Section - Fluid Design with Yellow/Red */}
        <div className="mt-16 relative overflow-hidden rounded-3xl p-10 md:p-12 text-center shadow-2xl bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-500/20">
          {/* Organic Fluid Shapes - Yellow/Red */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Large fluid blob - top left */}
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{
              background: 'radial-gradient(circle, #eab308 0%, transparent 70%)'
            }}></div>
            
            {/* Medium fluid blob - bottom right */}
            <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{
              background: 'radial-gradient(circle, #dc2626 0%, transparent 70%)'
            }}></div>
            
            {/* Small fluid blob - center */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-15 blur-2xl" style={{
              background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)'
            }}></div>
            
            {/* Floating shapes */}
            <div className="absolute top-10 right-20 w-40 h-40 rounded-full opacity-10 blur-2xl animate-pulse" style={{
              background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)',
              animationDuration: '4s'
            }}></div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/10 backdrop-blur-sm rounded-2xl mb-6 border border-yellow-500/30">
              <ChevronRight className="w-8 h-8 text-yellow-500" />
            </div>

            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Sie suchen eine spezielle Leistung?
            </h3>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie uns für eine individuelle Beratung. Wir finden gemeinsam die beste Lösung für Ihr Projekt.
            </p>
            
            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="group relative inline-flex items-center space-x-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-red-500/50 transition-all duration-300 hover:scale-105"
              >
                <span>Jetzt unverbindlich anfragen</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              
              {/* Secondary Info */}
              <div className="flex items-center space-x-2 text-white">
                <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                <span className="text-sm font-semibold">Kostenlose Erstberatung</span>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-6 text-gray-300 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="text-yellow-500 font-bold text-xs">✓</span>
                </div>
                <span>15+ Jahre Erfahrung</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="text-yellow-500 font-bold text-xs">✓</span>
                </div>
                <span>500+ Zufriedene Kunden</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="text-yellow-500 font-bold text-xs">✓</span>
                </div>
                <span>Faire Preise</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};